import { i18n } from "@app/i18n";
import { t } from "@lingui/core/macro";
import { toast } from "@app/ui";
import { RATE_LIMITED_PATH, navigate } from "./Pathname";
import { captureException } from "./Analytics";

// rate limit(429)에 걸렸을 때 사용자에게 안내하는 공통 지점. 서버가 인바운드 요청을
// 제한하면(server/core 의 RL_API/RL_SENSITIVE, better-auth 내장 rateLimit) 429 가
// 온다. 우리 /api/* 를 때리는 모든 요청은 이 모듈의 apiFetch 를 써서 한 곳에서 429
// 안내를 잡는다 — apiClient/authClient 는 fetch 구현으로 apiFetch 를 주입하고, RPC
// 를 안 거치는 raw 요청(queries·hooks·layouts 등)은 apiFetch 로 직접 호출한다.
// (전역 window.fetch 를 몽키패치하지 않는다 — 써드파티 요청까지 삼키고 스트림/재시도
//  개입 위험이 있어 명시적 래퍼로 대체했다.)

/** 안내 토스트가 짧은 시간에 겹쳐 쌓이지 않게 하는 최소 간격(ms). 한 번 429 가
 *  나면 보통 뒤이은 요청들도 연달아 429 라, 토스트가 여러 개 뜨는 걸 막는다. */
const DEDUP_MS = 3000;
let lastShownAt = 0;

/** 429 안내 토스트를 (중복 억제하며) 띄운다. */
export function notifyRateLimited(): void {
  const now = Date.now();
  if (now - lastShownAt < DEDUP_MS) return;
  lastShownAt = now;
  toast.error(t(i18n)`요청이 너무 많아요. 잠시 후 다시 시도해주세요.`);
}

/** 429 후 실제로 기다렸다 재시도하는 간격(ms) — 로직값. 세 rate limiter(RL_API
 *  600/60s, RL_SENSITIVE 10/60s, better-auth 내장 30/60s)가 모두 period 60초 fixed
 *  window 라, 창이 리셋될 때까지 최대 60초 남을 수 있다. 그래도 긴 대기로 붙잡아 두는
 *  대신 10초만 기다렸다 원래 경로로 복귀해 재시도한다 — 아직 안 풀렸으면 복귀 화면이
 *  다시 429 를 받아 이 화면으로 자동 재진입해 또 기다리므로(pages/rate-limited), 풀린
 *  경우엔 훨씬 빨리 돌아온다. 서버가 Retry-After 를 주면 그 값을 우선한다(아래 clamp). */
export const RATE_LIMIT_RETRY_MS = 10_000;

/** 화면(pages/rate-limited)이 카운트다운/progress bar 로 보여주는 표시 시간(ms) — UX값.
 *  실제 재시도 간격(RATE_LIMIT_RETRY_MS)과 지금은 같은 10초지만, "실제로 얼마나 기다리나"
 *  와 "사용자에게 몇 초로 보여주나"는 별개 개념이라 의도적으로 상수를 분리한다(한쪽만
 *  조정해도 서로 안 엮이도록). 정상 429 진입 시엔 저장된 실제 대기값을 쓰고, 이 값은
 *  상태 소실(예: /rate-limited 새로고침) fallback 표시에만 쓰인다. */
export const RATE_LIMIT_DISPLAY_MS = 10_000;

/** Retry-After 로 받은 대기시간을 이 범위로 clamp 한다 — 0초(즉시 복귀→도로 429)나
 *  비상식적으로 긴 값이 오는 걸 막는다. 상한은 window(60초)에 맞춘다. */
const MIN_WAIT_MS = 5_000;
const MAX_WAIT_MS = 60_000;

/** 대기 화면(pages/rate-limited)이 마감 시각·복귀 경로를 읽어가는 sessionStorage 키.
 *  탭 단위 임시 상태라 localStorage 가 아니라 sessionStorage 에 둔다. */
const RATE_LIMIT_STATE_KEY = "withtask.rate-limited";

/** 대기 화면이 필요로 하는 상태. from=복귀할 원래 경로, until=마감 시각(ms epoch),
 *  durationMs=progress bar 를 채우는 전체 대기시간. */
export type RateLimitState = { from: string; until: number; durationMs: number };

/** 429 응답 헤더(Retry-After / X-Retry-After)에서 대기시간(ms)을 뽑는다. 초 정수만
 *  다룬다(HTTP-date 형식은 이 서버에서 쓰지 않는다). 없거나 파싱 불가면 기본값. */
function waitMsFromResponse(res: Response): number {
  const header = res.headers.get("Retry-After") ?? res.headers.get("X-Retry-After");
  const seconds = header ? Number(header) : NaN;
  if (!Number.isFinite(seconds) || seconds <= 0) return RATE_LIMIT_RETRY_MS;
  return Math.min(MAX_WAIT_MS, Math.max(MIN_WAIT_MS, seconds * 1000));
}

/**
 * 429 를 만나면 현재 화면을 대기 화면(/rate-limited)으로 전환한다. 세션 조회
 * (get-session)가 429 나면 App.tsx 게이트가 "세션 없음"으로 오인해 로그인으로 튕기는
 * 문제를, 로그인보다 먼저 이 화면으로 가로채 막는다. 원래 경로를 저장해 뒀다가 대기
 * 후 복귀시킨다(pages/rate-limited/Page.tsx).
 */
function enterRateLimited(res: Response): void {
  // 이미 대기 화면이면 재진입하지 않는다 — 대기 화면은 요청을 안 하므로 여기 도달할
  // 일이 거의 없지만, 저장해 둔 원래 경로(from)를 /rate-limited 로 덮어써 복귀를
  // 깨뜨리는 것을 확실히 막는다.
  if (window.location.pathname === RATE_LIMITED_PATH) return;
  const durationMs = waitMsFromResponse(res);
  const state: RateLimitState = {
    from: window.location.pathname + window.location.search,
    until: Date.now() + durationMs,
    durationMs,
  };
  window.sessionStorage.setItem(RATE_LIMIT_STATE_KEY, JSON.stringify(state));
  navigate(RATE_LIMITED_PATH);
}

/** 대기 화면이 저장된 상태를 읽는다. 없거나 깨졌으면 null. */
export function readRateLimitState(): RateLimitState | null {
  const raw = window.sessionStorage.getItem(RATE_LIMIT_STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as RateLimitState;
    if (typeof parsed.from !== "string" || typeof parsed.until !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 대기 종료(복귀) 시 저장 상태를 지운다. */
export function clearRateLimitState(): void {
  window.sessionStorage.removeItem(RATE_LIMIT_STATE_KEY);
}

/** 워치독 임계값(ms). 이 시간을 넘겨도 요청이 안 끝나면 "체감 지연"으로 PostHog 에
 *  한 번 보고한다. 값이 비직관적이라 상수로 뺀다 — 이건 어보트 타임아웃이 *아니라*
 *  순수 관측용이라, 3초 넘는 정상 요청(대용량 업로드 등)을 끊지 않는다(소프트 워치독). */
const SLOW_REQUEST_MS = 3000;

/** 3초 초과가 정상인 엔드포인트 — 업로드/멀티파트/익스포트는 워치독을 걸지 않는다.
 *  느린 게 곧 이상신호가 아니라 그 자체가 정상 동작이라, 켜두면 노이즈만 된다.
 *  정규화된 path(normalizePath) 의 prefix 로 매칭한다. */
const SLOW_WATCHDOG_SKIP_PREFIXES = ["/api/uploads", "/api/upload", "/api/export"];

/** 볼륨 폭주 방지 — 네트워크가 나쁜 한 사용자가 같은 엔드포인트로 수백 건을 쏘지
 *  않도록, 이 세션에서 (정규화된) 엔드포인트당 1회만 보고한다. react-query retry:1 로
 *  같은 요청이 두 번 느려도 여기서 자연 디바운스된다. 드롭 시 log 로 사실을 남긴다. */
const reportedSlowEndpoints = new Set<string>();

/** URL 세그먼트가 id(태스크 id·uuid·nanoid 등)인지 — PII 이자 고카디널리티라, 이슈가
 *  엔드포인트 단위로 묶이고 id 단위로 쪼개지지 않게 :id 로 치환하기 위한 판별. */
function isIdSegment(seg: string): boolean {
  return /^\d+$/.test(seg) || /^[0-9a-zA-Z_-]{16,}$/.test(seg);
}

/** 쿼리스트링 제거(pathname) + id 세그먼트 :id 치환. PostHog 에는 이 정규화된 경로만
 *  올려 PII 를 빼고 fingerprint 카디널리티가 id 마다 폭발하지 않게 한다. */
function normalizePath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => (isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

/** input/init 에서 method 와 (정규화된) path 를 뽑는다. input 은 string|URL|Request 셋 다 온다. */
function extractRequest(input: RequestInfo | URL, init?: RequestInit): { method: string; path: string } {
  const rawUrl =
    typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const method = (
    init?.method ?? (input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  let pathname = rawUrl;
  try {
    pathname = new URL(rawUrl, window.location.origin).pathname;
  } catch {
    // 파싱 불가 시 원본 문자열을 그대로 두고 정규화만 태운다(방어).
  }
  return { method, path: normalizePath(pathname) };
}

/** 요청과 함께 3초 타이머를 건다. 요청이 먼저 끝나면 호출부가 clearTimeout 한다.
 *  타이머가 먼저 터지면 "이 엔드포인트가 느리다"를 PostHog Error tracking 에 1회 보고하고,
 *  요청 흐름 자체(성공/실패/취소)는 건드리지 않는다 — 부수 효과로만 동작한다. */
function startSlowWatchdog(input: RequestInfo | URL, init?: RequestInit): ReturnType<typeof setTimeout> {
  const { method, path } = extractRequest(input, init);
  return setTimeout(() => {
    // 오프라인은 "느린 API"가 아니라 별개 상태다(onlineManager/OfflineToast 가 처리) — 발화 안 함.
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    // 의도적으로 오래 걸리는 엔드포인트(업로드/익스포트)는 제외한다.
    if (SLOW_WATCHDOG_SKIP_PREFIXES.some((prefix) => path.startsWith(prefix))) return;
    const key = `${method} ${path}`;
    // 세션·엔드포인트당 1회 상한 — 초과분은 조용히 버리되 사실은 남긴다.
    if (reportedSlowEndpoints.has(key)) {
      log(`[slow-watchdog] drop (already reported this session): ${key}`);
      return;
    }
    reportedSlowEndpoints.add(key);
    const err = new ApiTimeoutError(method, path, SLOW_REQUEST_MS);
    captureException(err, {
      source: "api-timeout-watchdog",
      // 엔드포인트 단위 그룹핑 — 한 엔드포인트의 느림은 한 이슈로 모이고, 서로 다른
      // 엔드포인트는 분리된다(전부 한 줄에서 캡처해 단일 이슈로 뭉치는 함정 회피).
      $exception_fingerprint: `api-timeout:${method}:${path}`,
      method,
      path,
      // 아직 진행 중인 채로 보고하므로 실제 소요가 아니라 임계값을 넣는다(요청은 계속 진행).
      elapsedMs: SLOW_REQUEST_MS,
    });
  }, SLOW_REQUEST_MS);
}

/** log 미제공 환경(테스트 등) 방어용 얇은 래퍼. */
function log(message: string): void {
  if (typeof console !== "undefined") console.warn(message);
}

/**
 * 우리 /api/* 요청용 fetch. 네이티브 fetch 를 감싸 429 응답이면 안내 토스트를 띄우고
 * 대기 화면(/rate-limited)으로 전환한다. 응답 자체는 그대로 흘려보내 호출부의 기존
 * 에러 처리(res.ok 검사 등)를 방해하지 않는다 — 안내와 화면 전환은 부가 효과다.
 * 시그니처가 `typeof fetch` 라 raw fetch 의 drop-in 대체로 쓸 수 있고,
 * apiClient/authClient 의 fetch 구현으로도 주입한다.
 *
 * 추가로 3초 소프트 워치독을 건다(startSlowWatchdog) — 요청이 3초를 넘기면 "체감 지연"을
 * PostHog 에 1회 보고한다. 요청을 어보트하지 않으므로 기존 동작에 완전히 하위호환이고,
 * 요청이 settle 되면(finally) 반드시 clearTimeout 해 취소/완료를 느림으로 오탐하지 않는다.
 */
export const apiFetch: typeof fetch = async (input, init) => {
  const watchdog = startSlowWatchdog(input, init);
  try {
    const res = await fetch(input, init);
    if (res.status === 429) {
      notifyRateLimited();
      enterRateLimited(res);
    }
    return res;
  } finally {
    clearTimeout(watchdog);
  }
};

/**
 * 429 로 중단된 요청을 나타내는 에러. 안내 토스트는 이미 apiFetch 가
 * 띄웠으므로, 이 에러를 잡는 호출부는 "가입 실패" 같은 일반 에러 토스트를 겹쳐
 * 띄우지 말고 조용히 종료하면 된다.
 */
export class RateLimitError extends Error {
  constructor() {
    super("rate-limited");
    this.name = "RateLimitError";
  }
}

/**
 * 요청이 SLOW_REQUEST_MS 를 넘겨 "체감 지연"으로 관측된 것을 나타내는 에러. 실제로
 * 요청을 중단시키는 게 아니라(소프트 워치독) PostHog Error tracking 에 이슈로 올리기
 * 위한 마커다 — startSlowWatchdog 이 captureException 에 넘겨 엔드포인트 단위로
 * 그룹핑한다. RateLimitError 와 같은 위치·패턴으로 둔다.
 */
export class ApiTimeoutError extends Error {
  constructor(
    readonly method: string,
    readonly path: string,
    readonly elapsedMs: number,
  ) {
    super(`API request exceeded ${elapsedMs}ms: ${method} ${path}`);
    this.name = "ApiTimeoutError";
  }
}
